import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DateObject,
  DatePickerRef,
  MapDaysProps,
  Value,
} from "react-multi-date-picker";
import SingleDate, { SingleDateProps } from "./SingleDate";
import styled from "styled-components";
import { FLEX } from "styles/flex";
import moment from "moment";
import { LiaCalendarSolid } from "react-icons/lia";
import { COLORS, LIGHT_THEME_COLORS } from "styles/colors";
import { WithTooltip } from "components/blocks/WithTooltip";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { calcRem } from "styles/theme";

interface Props extends Omit<SingleDateProps, "onChange" | "value"> {
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  tooltip?: string;
  timezone?: string;
  isValidated?: boolean;
  endOfDay?: boolean;
  "data-testid"?: string;
}
type DateObjectType = (DateObject | null)[] | DateObject | DateObject[] | null;

export default function InputDate(props: Props) {
  const { onChange, value, tooltip, timezone, "data-testid": dataTestId = "atoms-calendar-input-date", ...rest } = props;
  const [inputValue, setInputValue] = useState<DateObjectType>(null);
  const [isValidated, setIsValidated] = useState(props.isValidated);
  const singleDateRef = useRef<DatePickerRef>(null);
  const lastDateRef = useRef<DatePickerRef>(null);
  const theme = useAppSelector((state) => state.theme);
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const [start, end] = useMemo(
    () =>
      Array.isArray(inputValue)
        ? inputValue[0] &&
          inputValue[1] &&
          moment(inputValue[0].toDate()).diff(
            moment(inputValue[1].toDate()),
            "seconds"
          ) > 0
          ? [inputValue[1], inputValue[0]]
          : [inputValue[0], inputValue[1]]
        : [null, null],
    [inputValue]
  );
  const convertValue = (v: DateObject | null, index: number = 0) => {
    const date = moment(v?.toDate()).format("YYYY-MM-DD HH:mm:ss");
    if (!date) return "";
    if (props.allowTime) {
      return moment
        .tz(date, timezone || selectedAccount?.timezone)
        .utc()
        .format("YYYY-MM-DDTHH:mm:ss");
    }
    return moment
      .tz(date, timezone || selectedAccount?.timezone)
      [index === 1 ? "endOf" : "startOf"]("day")
      .utc()
      .format("YYYY-MM-DDTHH:mm:ss");
  };
  const handleChange = (value: DateObjectType) => {
    if (Array.isArray(value)) {
      if (value?.[0] && value?.[1] && props.allowTime) {
        const sortedValue = value.sort((a, b) =>
          moment(a?.toDate()).diff(moment(b?.toDate()))
        );
        setInputValue(sortedValue);
        onChange?.(sortedValue.map((v, index) => convertValue(v, index)));
      } else {
        setInputValue(value);
        setIsValidated(false);
        if (value.length === 2) {
          onChange?.(
            value.map((v, index) => (v ? convertValue(v, index) : ""))
          );
        }
      }
    } else if (value) {
      setInputValue(value);
      onChange?.(convertValue(value, props.endOfDay ? 1 : 0));
    }
  };
  const setValue = useCallback(
    (value: string | string[]) => {
      const convertedValue = (value: string) => {
        const date = getAccountTimeFormatDate(
          value,
          {
            ...selectedAccount,
            timezone: timezone || selectedAccount?.timezone,
            dateFormat: "YYYY-MM-DD",
            timeFormat: "HH:mm:ss",
          },
          false,
          props.allowTime
        );
        if (!date) {
          const fallback = new Date(value);
          if (Number.isNaN(fallback.getTime())) {
            return null;
          }
          return new DateObject(fallback);
        }
        return new DateObject(date);
      };

      if (Array.isArray(value)) {
        setInputValue(value.map((v) => convertedValue(v as string)));
      } else {
        setInputValue(convertedValue(value as string));
      }
    },
    [props.allowTime, selectedAccount, timezone]
  );

  useEffect(() => {
    if (value && !inputValue) {
      setValue(value);
    }
  }, [value, setValue, inputValue]);
  useEffect(() => {
    return () => {
      setInputValue(null);
    };
  }, []);

  const mayDay = useCallback(
    ({
      date,
      today,
      selectedDate,
      currentMonth,
      isSameDate,
    }: MapDaysProps<false, false>) => {
      let props: React.HTMLAttributes<HTMLSpanElement> & {
        disabled?: boolean;
        hidden?: boolean;
      } = {};

      if (
        (start && isSameDate(date, start)) ||
        (end && isSameDate(date, end)) ||
        (end &&
          moment(end.toDate()).isAfter(date.toDate()) &&
          start &&
          moment(start.toDate()).isBefore(date.toDate()))
      ) {
        props.style = {
          backgroundColor:
            theme.theme === "light"
              ? LIGHT_THEME_COLORS.BUTTONS2
              : COLORS.BUTTONS2,
          color: COLORS.WHITE,
          borderRadius: 0,
        };
        props.style = {
          ...props.style,
          borderTopRightRadius: end && isSameDate(date, end) ? "50%" : "0",
          borderTopLeftRadius: start && isSameDate(date, start) ? "50%" : "0",
          borderBottomRightRadius: end && isSameDate(date, end) ? "50%" : "0",
          borderBottomLeftRadius:
            start && isSameDate(date, start) ? "50%" : "0",
        };
      }
      return props;
    },
    [start, end, theme.theme]
  );

  if (props.mode === "range") {
    return (
      <DateRangeWrapper.Container
        disabled={props.disabled}
        isValidated={isValidated}
        className="date-range-wrapper date-picker-wrapper"
      >
        {tooltip ? (
          <WithTooltip tooltip={tooltip}>
            <LiaCalendarSolid className="calendar-icon" size={18} />
          </WithTooltip>
        ) : (
          <LiaCalendarSolid className="calendar-icon" size={18} />
        )}
        <span>From</span>
        <SingleDate
          {...rest}
          value={start}
          ref={singleDateRef}
          isValidated={isValidated}
          onChange={(date) => {
            handleChange([date as DateObject, end]);
          }}
          mapDays={mayDay}
        />
        <span className="to-label">To</span>
        <SingleDate
          {...rest}
          value={end}
          ref={lastDateRef}
          isValidated={isValidated}
          onChange={(value) => {
            handleChange([start, value as DateObject]);
          }}
          minDate={start?.toDate()}
          mapDays={mayDay}
        />
      </DateRangeWrapper.Container>
    );
  }
  return (
    <DateRangeWrapper.Container
      disabled={props.disabled}
      isValidated={isValidated}
      className="date-single-wrapper date-picker-wrapper"
    >
      <LiaCalendarSolid
        size={18}
        onClick={() => singleDateRef.current?.openCalendar()}
        data-testid={`${dataTestId}-calendar-icon`}
      />
      <SingleDate
        {...rest}
        value={inputValue as Value}
        ref={singleDateRef}
        isValidated={isValidated}
        onChange={(value) => {
          handleChange(value as DateObject);
        }}
        data-testid={dataTestId}
      />
    </DateRangeWrapper.Container>
  );
}

const DateRangeWrapper = {
  Container: styled.div<{ disabled?: boolean; isValidated?: boolean }>`
    position: relative;
    ${FLEX.FlexStartCenter};
    gap: 2px;
    background-color: #fff;
    border-radius: 3px;
    border: 1px solid rgb(202 202 202);
    padding: 0 10px;
    color: ${COLORS.TEXT};
    font-size: ${calcRem(14)};
    ${({ isValidated }) =>
      isValidated &&
      `
        border: 1px solid ${COLORS.VALIDATERED};
        background-color: #FFD5D5;
        color: ${COLORS.VALIDATERED};
    `}
    ${({ disabled, theme }) =>
      disabled &&
      `
        background-color: ${theme.COLORS.DISABLED};
        border-color: ${theme.COLORS.DISABLED_BORDER};
        cursor: not-allowed;
    `}

    .to-label {
      margin-left: 2px;
    }
    .rmdp-input:focus {
      border: 0;
    }
    .calendar-icon {
      display: block;
    }

    .rmdp-container,
    .date-input-wrapper:first-of-type:last-of-type {
      flex: 1;
    }
    .rmdp-day span {
      left: 0;
      top: 0;
      right: 0;
      bottom: 0;
    }
  `,
};
